import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { trackContact } from "@/lib/analytics";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: {
    overline: "Parlons de votre projet", title: "Contactez-nous",
    keep: "Restons en contact", intro: "Une question sur nos packs ou votre éligibilité ? Écrivez-nous, notre équipe vous répond rapidement.",
    lblEmail: "Email", lblWhats: "WhatsApp", lblAddr: "Adresse", addr: "Siège social — Floride, États-Unis",
    hours: "Nos horaires", hoursVal: "Lundi au vendredi : 09h00 – 18h00 (heure de Paris)", hoursNote: "Nous nous efforçons de répondre à toutes les demandes dans les meilleurs délais.",
    name: "Nom complet", nameP: "Votre nom", email: "Email", phone: "Téléphone", subject: "Sujet", subjectP: "Objet de votre message",
    message: "Message", messageP: "Décrivez votre projet…", send: "Envoyer le message", sending: "Envoi…",
    ok: "Votre message a bien été envoyé !",
  },
  en: {
    overline: "Let's talk about your project", title: "Contact us",
    keep: "Stay in touch", intro: "A question about our packages or your eligibility? Write to us, our team replies quickly.",
    lblEmail: "Email", lblWhats: "WhatsApp", lblAddr: "Address", addr: "Head office — Florida, USA",
    hours: "Our hours", hoursVal: "Monday to Friday: 9:00 AM – 6:00 PM (Paris time)", hoursNote: "We strive to answer all requests as quickly as possible.",
    name: "Full name", nameP: "Your name", email: "Email", phone: "Phone", subject: "Subject", subjectP: "Subject of your message",
    message: "Message", messageP: "Describe your project…", send: "Send message", sending: "Sending…",
    ok: "Your message has been sent!",
  },
};

export default function Contact() {
  const { lang } = useLang();
  const c = C[lang];
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      trackContact();
      toast.success(c.ok);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact — Work & Travel Expert | Parlons de votre projet Australie ou Canada"
        description="Une question sur nos packs ou votre éligibilité au visa Australie ou Canada ? Contactez l'équipe Work & Travel Expert par email ou WhatsApp, réponse rapide garantie."
        keywords="contact work travel expert, éligibilité visa, conseil immigration australie canada"
        path="/contact"
      />
      <div className="bg-navy">
        <Section className="py-24 text-center">
          <Overline>{c.overline}</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-medium text-white">{c.title}</h1>
        </Section>
      </div>

      <Section className="py-20 grid lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-2">
          <h2 className="font-heading text-3xl text-navy">{c.keep}</h2>
          <div className="gold-line mt-5" />
          <p className="mt-6 text-slate-700 leading-relaxed">{c.intro}</p>
          <div className="mt-10 space-y-6">
            {[
              { icon: Mail, label: c.lblEmail, value: "worktravelexpert@hotmail.com", href: "mailto:worktravelexpert@hotmail.com" },
              { icon: Phone, label: c.lblWhats, value: "+33 7 49 85 75 87", href: "https://wa.me/33749857587" },
              { icon: MapPin, label: c.lblAddr, value: c.addr },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-4">
                <div className="h-11 w-11 shrink-0 bg-navy text-gold flex items-center justify-center"><item.icon size={20} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-gold font-semibold">{item.label}</div>
                  {item.href
                    ? <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-navy mt-1 block hover:text-gold transition-colors">{item.value}</a>
                    : <div className="text-navy mt-1">{item.value}</div>}
                </div>
              </div>
            ))}
          </div>

          <div data-testid="horaires" className="mt-10 bg-navy text-white p-6">
            <div className="flex items-center gap-3"><Clock size={20} className="text-gold" /><h3 className="font-heading text-xl">{c.hours}</h3></div>
            <p className="mt-4 text-white/85 text-sm">{c.hoursVal}</p>
            <p className="mt-2 text-white/60 text-sm">{c.hoursNote}</p>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-border p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><Label className="text-navy">{c.name}</Label>
                <Input required data-testid="contact-name" value={form.name} onChange={upd("name")} className="mt-2 rounded-none" placeholder={c.nameP} /></div>
              <div><Label className="text-navy">{c.email}</Label>
                <Input required type="email" data-testid="contact-email" value={form.email} onChange={upd("email")} className="mt-2 rounded-none" placeholder="vous@email.com" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><Label className="text-navy">{c.phone}</Label>
                <Input data-testid="contact-phone" value={form.phone} onChange={upd("phone")} className="mt-2 rounded-none" placeholder="+33 …" /></div>
              <div><Label className="text-navy">{c.subject}</Label>
                <Input required data-testid="contact-subject" value={form.subject} onChange={upd("subject")} className="mt-2 rounded-none" placeholder={c.subjectP} /></div>
            </div>
            <div><Label className="text-navy">{c.message}</Label>
              <Textarea required data-testid="contact-message" value={form.message} onChange={upd("message")} rows={5} className="mt-2 rounded-none" placeholder={c.messageP} /></div>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-gold hover:bg-gold-hover text-white py-4 font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {loading ? c.sending : <>{c.send} <Send size={16} /></>}
            </button>
          </form>
        </Reveal>
      </Section>
    </div>
  );
}
